import { morphism } from 'morphism'
import { get } from 'lodash-es'
import { toLower, uniq } from 'ramda'
import { currencyUtils } from '../../../utils'
import { commissionUndertakings, paymentRuleClearanceTypes } from './constants'
import {
  TApplicationsResponse,
  TCommissionFee,
  TCreateApplicationRequest,
  TInvoiceTemplate,
  TPaymentRuleClearance,
  TPortfolio,
  TPortfolioListEntry,
  TPortfolioSummariesResponse,
  TPortfolioSplit,
  TPortfolioSummary,
  TRenewal,
  TUpdateApplicationRequest,
} from './types'

const { toCents, fromCents } = currencyUtils

/**
 * The wire carries rands, the store carries cents. `convertToCents` picks the direction:
 * responses come in (`true`), requests go out (`false`).
 */
const transformPaymentRuleClearance = (clearance: TPaymentRuleClearance, convertToCents = true) => {
  switch (clearance.type) {
    case paymentRuleClearanceTypes.fixed:
      return {
        ...clearance,
        value: {
          ...clearance.value,
          grossAmount: convertToCents
            ? toCents(get(clearance, 'value.grossAmount')).getAmount()
            : fromCents(get(clearance, 'value.grossAmount')),
        },
      }
    default:
      return clearance
  }
}

/**
 * A commission fee as it arrives on the wire, before the `ofRentalAmount` wrapper is
 * flattened.
 */
type TWireCommissionFee = {
  ofRentalAmount?: {
    type?: string
    value?: { netAmount?: number; percentage?: number; vatable?: boolean }
  }
  splits?: TPortfolioSplit[]
}

/**
 * A commission fee as the forms hold it: the flattened {@link TCommissionFee}, except that
 * each split carries the raw select-option (`agent: { value }`) rather than an agentPartyId.
 */
type TFormCommissionFee = Omit<TCommissionFee, 'splits'> & {
  splits?: { agent?: { value?: string }; splitPercentage?: number; isPrimary?: boolean }[]
}

/**
 * Flattens the `ofRentalAmount: { type, value }` wrapper into the discriminated
 * {@link TCommissionFee}. A fee whose undertaking type is neither fixed nor variable is
 * passed through untouched (it is either absent, or a shape this client does not model).
 */
const transformCommissionFee = (fee: TWireCommissionFee | undefined, convertToCents = true): TCommissionFee => {
  const undertaking = fee?.ofRentalAmount
  const splits = fee?.splits || []
  const vatable = undertaking?.value?.vatable ?? false

  switch (undertaking?.type) {
    case commissionUndertakings.fixed:
      return {
        type: 'FixedUndertaking_v1',
        netAmount: {
          value: convertToCents
            ? toCents(undertaking?.value?.netAmount).getAmount()
            : fromCents(undertaking?.value?.netAmount),
          vatable,
        },
        splits,
      }
    case commissionUndertakings.variable:
      return {
        type: 'VariableUndertaking_v1',
        percentage: {
          value: undertaking?.value?.percentage ?? 0,
          vatable,
        },
        splits,
      }
    default:
      return fee as unknown as TCommissionFee
  }
}

const getPortfolioResponseSchema = {
  id: 'id',
  createdByUserId: 'createdByUserId',
  updatedByUserId: 'updatedByUserId',
  createdAt: 'createdAt',
  deletedAt: 'deletedAt',
  updatedAt: 'updatedAt',
  tags: 'tags',
  propertyId: 'propertyId',
  /** @todo handle unmanaged */
  contractContainer: ({ contractContainer }: { contractContainer?: unknown }) => {
    const type = get(contractContainer, 'type', '')
    const terms = get(contractContainer, 'value.terms', {})
    return (
      contractContainer && {
        type,
        value: {
          terms: {
            keyDepositAmount: toCents(get(terms, 'keyDepositAmount', 0)).getAmount(),
            serviceDepositAmount: toCents(get(terms, 'serviceDepositAmount', 0)).getAmount(),
            damageDepositAmount: toCents(get(terms, 'damageDepositAmount', 0)).getAmount(),
            monthlyRentAmount: toCents(get(terms, 'monthlyRentAmount', 0)).getAmount(),
            firstMonthRentAmount: toCents(get(terms, 'firstMonthRentAmount', 0)).getAmount(),
            leaseFee: {
              netAmount: toCents(get(terms, 'leaseFee.netAmount', 0)).getAmount(),
              vatable: get(terms, 'leaseFee.vatable', false),
            },
            applicationFee: {
              netAmount: toCents(get(terms, 'applicationFee.netAmount', 0)).getAmount(),
              vatable: get(terms, 'applicationFee.vatable', false),
            },
            administrationFee: {
              netAmount: toCents(get(terms, 'administrationFee.netAmount', 0)).getAmount(),
              vatable: get(terms, 'administrationFee.vatable', false),
            },
            depositHeldByLandlord: get(terms, 'depositHeldByLandlord', false),
          },
        },
      }
    )
  },
  parties: 'parties',
  invoiceTemplates: ({ invoiceTemplates }: { invoiceTemplates?: TInvoiceTemplate[] }) =>
    invoiceTemplates?.map(template => ({
      ...template,
      netAmount: toCents(get(template, 'netAmount', 0)).getAmount(),

      paymentRules: template.paymentRules.map(rule => ({
        beneficiary: {
          ...rule.beneficiary,
          value: {
            ...rule.beneficiary.value,
            amount: toCents(rule.beneficiary.value.amount).getAmount(),
          },
        },

        clearance: transformPaymentRuleClearance(rule.clearance),
      })),
    })),
  agents: 'agents',
  leaseIdentifier: 'leaseIdentifier',
  leaseTerms: 'leaseTerms',
  settings: 'settings',
  segments: ({ segments = [] }: { segments?: string[] }) => uniq(segments),
  commission: ({
    commission,
  }: {
    commission?: { managementFee?: TWireCommissionFee; procurementFee?: TWireCommissionFee }
  }) =>
    commission && {
      managementFee: transformCommissionFee(commission.managementFee),
      procurementFee: transformCommissionFee(commission.procurementFee),
    },
  renewal: ({
    renewal,
  }: {
    renewal?: Omit<TRenewal, 'newCommission'> & {
      newCommission?: { managementFee?: TWireCommissionFee; procurementFee?: TWireCommissionFee }
    }
  }) =>
    renewal && {
      newRentAmount: toCents(renewal.newRentAmount).getAmount(),
      newCommission: {
        managementFee: transformCommissionFee(renewal.newCommission?.managementFee),
        procurementFee: transformCommissionFee(renewal.newCommission?.procurementFee),
      },
      depositTopUp: toCents(renewal.depositTopUp).getAmount(),
      renewalFee: toCents(renewal.renewalFee).getAmount(),
      renewalFeeVatable: renewal.renewalFeeVatable,
      leaseRenewsAt: renewal.leaseRenewsAt,
      newLeaseTerms: renewal.newLeaseTerms,
    },
  terminationFields: 'terminationFields',
  metaData: 'metaData',
  status: ({ status = 'draft' }: { status?: string }) => toLower(status),
  applications: 'applications',
}

/**
 * `morphism` maps an array source element-wise, so this mirrors what the caller passed in.
 * `transformGetListResponse` relies on the array overload.
 */
export function transformGetPortfolioResponse(source: unknown[]): TPortfolio[]
export function transformGetPortfolioResponse(source: unknown): TPortfolio
export function transformGetPortfolioResponse(source: unknown): TPortfolio | TPortfolio[] {
  return morphism(getPortfolioResponseSchema, source as never) as unknown as TPortfolio | TPortfolio[]
}

const getSummarySchema = {
  agencyId: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.agencyId,
  portfolioId: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.portfolioId,
  propertyId: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.propertyId,
  mainText: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.mainText,
  secondaryText: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.secondaryText,
  primaryOwnerId: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.primaryOwnerId,
  primaryOwnerName: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.primaryOwnerName,
  primaryTenantId: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.primaryTenantId,
  primaryTenantName: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.primaryTenantName,
  tags: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.tags,
  landlords: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.landlords,
  tenants: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.tenants,
  segments: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => uniq(portfolioSummaryRow?.segments ?? []),
  leaseTerms: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.leaseTerms,
  /** See the note on `TPortfolioSummaryRow.monthlyRentAmount`. */
  monthlyRentAmount: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) =>
    portfolioSummaryRow?.monthlyRentAmount === undefined
      ? undefined
      : toCents(portfolioSummaryRow.monthlyRentAmount).getAmount(),
  renewal: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.renewal,
  deletedAt: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.deletedAt,
  status: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) =>
    portfolioSummaryRow.status ? toLower(portfolioSummaryRow.status) : 'draft',
  /**
   * Read from the top level of the response, *not* from `portfolioSummaryRow`, which has
   * no such field. See the note on `TPortfolioSummariesResponse.terminationFields`.
   */
  terminationFields: 'terminationFields',
  /** The row carries these; older responses only had them under `terminationFields`. */
  terminatedAt: ({ portfolioSummaryRow, terminationFields }: TPortfolioSummariesResponse) =>
    portfolioSummaryRow?.terminatedAt ?? terminationFields?.terminatedAt,
  vacateDate: ({ portfolioSummaryRow, terminationFields }: TPortfolioSummariesResponse) =>
    portfolioSummaryRow?.vacateDate ?? terminationFields?.vacateDate,
  metaData: ({ portfolioSummaryRow }: TPortfolioSummariesResponse) => portfolioSummaryRow?.metaData,
  applications: 'applications',
}

export const transformGetSummariesResponse = (source: TPortfolioSummariesResponse[]): TPortfolioSummary[] =>
  morphism(getSummarySchema, source as never) as unknown as TPortfolioSummary[]

const getListResponseSchema = {
  mainText: 'mainText',
  secondaryText: 'secondaryText',
  propertyId: 'propertyId',
  portfolios: ({ portfolios }: { portfolios: unknown[] }) => transformGetPortfolioResponse(portfolios),
}

export const transformGetListResponse = (source: unknown[]): TPortfolioListEntry[] =>
  morphism(getListResponseSchema, source as never) as unknown as TPortfolioListEntry[]

const amendManagedContractRequestSchema = {
  type: 'type',
  value: ({ value: { terms } }: { value: { terms: unknown } }) => {
    return {
      terms: {
        keyDepositAmount: fromCents(get(terms, 'keyDepositAmount', 0)),
        serviceDepositAmount: fromCents(get(terms, 'serviceDepositAmount', 0)),
        damageDepositAmount: fromCents(get(terms, 'damageDepositAmount', 0)),
        monthlyRentAmount: fromCents(get(terms, 'monthlyRentAmount', 0)),
        firstMonthRentAmount: fromCents(get(terms, 'firstMonthRentAmount', 0)),
        leaseFee: {
          netAmount: fromCents(get(terms, 'leaseFee.netAmount', 0)),
          vatable: get(terms, 'leaseFee.vatable', false),
        },
        applicationFee: {
          netAmount: fromCents(get(terms, 'applicationFee.netAmount', 0)),
          vatable: get(terms, 'applicationFee.vatable', false),
        },
        administrationFee: {
          netAmount: fromCents(get(terms, 'administrationFee.netAmount', 0)),
          vatable: get(terms, 'administrationFee.vatable', false),
        },
      },
    }
  },
}

export const transformAmendManagedContractRequest = (source: unknown) =>
  morphism(amendManagedContractRequestSchema, source as never)

const amendUnmanagedContractRequestSchema = {
  type: 'type',
  value: ({ value: { terms } }: { value: { terms: unknown } }) => {
    return {
      terms: {
        keyDepositAmount: fromCents(get(terms, 'keyDepositAmount', 0)),
        serviceDepositAmount: fromCents(get(terms, 'serviceDepositAmount', 0)),
        damageDepositAmount: fromCents(get(terms, 'damageDepositAmount', 0)),
        monthlyRentAmount: fromCents(get(terms, 'monthlyRentAmount', 0)),
        firstMonthRentAmount: fromCents(get(terms, 'firstMonthRentAmount', 0)),
        leaseFee: {
          netAmount: fromCents(get(terms, 'leaseFee.netAmount', 0)),
          vatable: get(terms, 'leaseFee.vatable', false),
        },
        applicationFee: {
          netAmount: fromCents(get(terms, 'applicationFee.netAmount', 0)),
          vatable: get(terms, 'applicationFee.vatable', false),
        },
        administrationFee: {
          netAmount: fromCents(get(terms, 'administrationFee.netAmount', 0)),
          vatable: get(terms, 'administrationFee.vatable', false),
        },
        depositHeldByLandlord: get(terms, 'depositHeldByLandlord', false),
        collectCommissionFromLandlord: get(terms, 'collectCommissionFromLandlord', false),
        invoiceDifferenceToLandlord: get(terms, 'invoiceDifferenceToLandlord', true),
      },
    }
  },
}

export const transformAmendUnmanagedContractRequest = (source: unknown) =>
  morphism(amendUnmanagedContractRequestSchema, source as never)

/**
 * Rebuilds the `ofRentalAmount: { type, value }` wrapper the API expects from the
 * flattened {@link TCommissionFee} the forms produce.
 */
const transformCommissionRequest = (commission: TFormCommissionFee | undefined | null) => {
  if (!commission) {
    return null
  }

  const value =
    commission.type === commissionUndertakings.fixed
      ? {
          netAmount: fromCents(get(commission, 'netAmount.value') || 0),
          vatable: get(commission, 'netAmount.vatable') || false,
        }
      : {
          percentage: get(commission, 'percentage.value') || 0,
          vatable: get(commission, 'percentage.vatable') || false,
        }

  return {
    ofRentalAmount: {
      type: commission.type,
      value,
    },
    // Changed this to accept empty splits
    splits: (commission.splits ?? []).map(split => ({
      agentPartyId: split?.agent?.value,
      splitPercentage: split?.splitPercentage,
      isPrimary: split?.isPrimary || false,
    })),
  }
}

const amendCommissionSchema = {
  managementFee: ({ managementFee }: { managementFee?: TFormCommissionFee }) =>
    managementFee && transformCommissionRequest(managementFee),
  procurementFee: ({ procurementFee }: { procurementFee?: TFormCommissionFee }) =>
    procurementFee && transformCommissionRequest(procurementFee),
}

export const transformAmendCommissionRequest = (source: unknown) => morphism(amendCommissionSchema, source as never)

const invoiceTemplateSchema = {
  category: 'category',
  invoicePartyId: 'invoicePartyId', // Either Tenant or Landlord.
  invoicePartyAccountType: 'invoicePartyAccountType', // partyApiConstants.accountTypes
  interval: 'interval', // BeginningOfMonth, EndOfMonth, MidMonth
  vatable: 'vatable',
  autoDeliver: 'autoDeliver',
  netAmount: ({ netAmount }: { netAmount: number }) => fromCents(netAmount),
  paymentRules: ({ paymentRules }: Pick<TInvoiceTemplate, 'paymentRules'>) =>
    paymentRules.map(({ beneficiary, clearance }) => ({
      beneficiary: {
        ...beneficiary,
        value: {
          ...beneficiary.value,
          amount: fromCents(beneficiary.value.amount),
        },
      },
      clearance: transformPaymentRuleClearance(clearance, false),
    })),
  description: 'description',
}

export const transformInvoiceTemplate = (source: unknown) => morphism(invoiceTemplateSchema, source as never)

const renewalRequestSchema = {
  newRentAmount: ({ newRentAmount }: { newRentAmount: number }) => fromCents(newRentAmount),
  newCommission: ({
    newCommission,
  }: {
    newCommission?: { managementFee?: TFormCommissionFee; procurementFee?: TFormCommissionFee }
  }) => ({
    managementFee: transformCommissionRequest(newCommission?.managementFee),
    procurementFee: transformCommissionRequest(newCommission?.procurementFee),
  }),
  depositTopUp: ({ depositTopUp }: { depositTopUp: number }) => fromCents(depositTopUp),
  renewalFee: ({ renewalFee }: { renewalFee?: number }) => fromCents(renewalFee),
  renewalFeeVatable: 'renewalFeeVatable',
  leaseRenewsAt: 'leaseRenewsAt',
  newLeaseTerms: 'newLeaseTerms',
}

export const transformRenewRequest = (source: unknown) => morphism(renewalRequestSchema, source as never)

// @todo Update these to include VAT

const applicationsResponseSchema = {
  type: 'type',
  payload: ({ payload }: { payload: TApplicationsResponse }) => ({
    portfolioId: payload.portfolioId,
    applications: payload.applications.map(application => ({
      ...application,
      applicationFee: toCents(application.applicationFee).getAmount(),
    })),
  }),
}

/**
 * Unlike the other transformers this one takes a whole *action* and rewrites its payload,
 * because the applications epics wrap the response in the success event before converting.
 */
export const transformApplicationsResponse = <A extends { type: string; payload: TApplicationsResponse }>(
  source: A,
): A => morphism(applicationsResponseSchema, source as never) as unknown as A

const addApplicationRequestSchema = {
  partyId: 'partyId',
  applicationFee: ({ applicationFee }: TCreateApplicationRequest) => fromCents(applicationFee),
}

export const transformAddApplicationRequest = (source: TCreateApplicationRequest) =>
  morphism(addApplicationRequestSchema, source)

const updateApplicationRequestSchema = {
  applicationFee: ({ applicationFee }: TUpdateApplicationRequest) => fromCents(applicationFee),
  vat: 'vat',
  partyId: 'partyId',
}

export const transformUpdateApplicationRequest = (source: TUpdateApplicationRequest) =>
  morphism(updateApplicationRequestSchema, source)
